// src/pages/profile/ProfileView.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useFuncion } from "../../context/FuncionContext";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../components/LoadingSpinner";

export default function ProfileView() {
  const { getMyProfile } = useFuncion();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        setErr(null);
        const res = await getMyProfile();
        setData(res);
      } catch (e) {
        console.error(e);
        setErr(e?.message || "No se pudo cargar el perfil.");
      } finally {
        setLoading(false);
      }
    })();
  }, [getMyProfile]);

  // Hooks SIEMPRE arriba
  const user = data?.user;
  const stats = data?.stats;
  const courses = data?.courses || [];

  const fullName = useMemo(() => {
    return (
      [user?.nombres, user?.apellidos].filter(Boolean).join(" ").trim() ||
      user?.nombre ||
      user?.email ||
      "Usuario"
    );
  }, [user?.nombres, user?.apellidos, user?.nombre, user?.email]);

  const joinedDate = useMemo(() => {
    return user?.joined_at
      ? new Date(user.joined_at).toLocaleDateString("es-EC", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : "—";
  }, [user?.joined_at]);

  const coins = useMemo(() => Number(stats?.coins_balance ?? 0), [stats?.coins_balance]);
  const streak = useMemo(() => Number(stats?.streak_days ?? 0), [stats?.streak_days]);

  // ✅ Progreso general REAL: promedio de progreso de todos los cursos (0..100)
  const overall = useMemo(() => {
    if (!courses.length) return 0;
    const sum = courses.reduce((acc, c) => acc + clamp(c?.progreso ?? 0), 0);
    return clamp(sum / courses.length);
  }, [courses]);

  const progressMeta = useMemo(() => {
    const total = courses.length;
    const completed = courses.filter((c) => clamp(c?.progreso ?? 0) >= 100).length;
    const started = courses.filter((c) => {
      const p = clamp(c?.progreso ?? 0);
      return p > 0 && p < 100;
    }).length;
    const notStarted = total - completed - started;

    return { total, completed, started, notStarted };
  }, [courses]);

  const topCourses = useMemo(() => {
    return [...courses].sort((a, b) => {
      const pa = Number(a?.progreso ?? 0);
      const pb = Number(b?.progreso ?? 0);
      if (pb !== pa) return pb - pa;

      const da = a?.ultimo_acceso ? new Date(a.ultimo_acceso).getTime() : 0;
      const db = b?.ultimo_acceso ? new Date(b.ultimo_acceso).getTime() : 0;
      return db - da;
    });
  }, [courses]);

  // returns después de hooks
  if (loading) return <LoadingSpinner message="Cargando perfil…" />;

  if (err || !data) {
    return (
      <div className="pvPage">
        <style>{baseCss}</style>
        <div className="pvCardCenter pvErrorBorder">
          <div style={{ fontSize: "3rem", marginBottom: 12 }}>⚠️</div>
          <h2 style={{ color: "#e11d48", marginBottom: 8 }}>Error al cargar el perfil</h2>
          <p style={{ color: "#64748b", marginBottom: 16 }}>{err || "Intenta nuevamente"}</p>
          <button onClick={() => window.location.reload()} className="pvBtnDanger">
            Recargar página
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pvPage">
      <style>{baseCss}</style>

      {/* Header */}
      <header className="pvHeader">
        <div className="pvHeaderTop">
          <div className="pvAvatarWrap" aria-hidden>
            <div className="pvAvatar">👤</div>
          </div>

          <div className="pvHeaderInfo">
            <div className="pvHeaderLine1">
              <div className="pvTitleWrap">
                <h1 className="pvTitle">{fullName}</h1>
                <div className="pvSub">
                  <span className="pvMuted">{user?.email || "—"}</span>
                  <span className="pvDot">•</span>
                  <span className="pvHideOnMobile">
                    Se unió el <strong>{joinedDate}</strong>
                  </span>
                  <span className="pvShowOnMobile">
                    <strong>{joinedDate}</strong>
                  </span>
                </div>
              </div>
            </div>

            {!!(user?.roles && user.roles.length) && (
              <div className="pvRoles">
                {user.roles.map((r) => (
                  <span key={r} className="pvRolePill">
                    {r}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Stats (coins + racha) */}
      <section className="pvStats">
        <div className="pvStatsRow">
          <div className="pvStatCard pvHoverLift">
            <div className="pvStatIcon pvCoinIcon" aria-hidden>
              💰
            </div>
            <div className="pvStatBody">
              <div className="pvStatValue">{coins.toLocaleString("es-EC")}</div>
              <div className="pvStatLabel">UTMCoins</div>
              <div className="pvStatHint">Saldo actual</div>
            </div>
          </div>

          <div className="pvStatCard pvHoverLift">
            <div className="pvStatIcon pvStreakIcon" aria-hidden>
              🔥
            </div>
            <div className="pvStatBody">
              <div className="pvStatValue">{streak} días</div>
              <div className="pvStatLabel">Racha</div>
              <div className="pvStatHint">Estudio continuo</div>
            </div>
          </div>
        </div>

        {/* Progreso general (solo UNA barra mejorada) */}
        <div className="pvProgressCard pvHoverLift">
          <div className="pvProgressHead">
            <div>
              <div className="pvProgressTitle">Progreso general</div>
              <div className="pvProgressSub">
                Promedio real de todos tus cursos ({progressMeta.total})
              </div>
            </div>

            <div className="pvProgressPctWrap">
              <div className="pvProgressPct">{Math.round(overall)}%</div>
              <div className="pvMutedTiny">total</div>
            </div>
          </div>

          {/* Barra principal PRO */}
          <div className="pvBarPro" aria-label="Progreso general">
            <div
              className="pvBarProFill"
              style={{ width: `${overall}%` }}
            />
          </div>

          {/* Resumen */}
          <div className="pvProgressMeta2">
            <div className="pvPill">
              <span className="pvPillK">Completos</span>
              <span className="pvPillV">{progressMeta.completed}</span>
            </div>
            <div className="pvPill">
              <span className="pvPillK">En progreso</span>
              <span className="pvPillV">{progressMeta.started}</span>
            </div>
            <div className="pvPill">
              <span className="pvPillK">Sin iniciar</span>
              <span className="pvPillV">{progressMeta.notStarted}</span>
            </div>
          </div>

          <div className="pvProgressFoot">
            <span className="pvMutedSmall">
              {streak > 0 ? "Mantén la racha para sumar progreso cada día." : "Empieza hoy y crea tu racha."}
            </span>
          </div>
        </div>
      </section>

      {/* Mis cursos */}
      <section className="pvCard">
        <div className="pvSectionHead">
          <div>
            <h2 className="pvSectionTitle">Mis cursos</h2>
            <p className="pvSectionSub">Ordenados por progreso. Continúa en un clic.</p>
          </div>

          {!!topCourses.length && (
            <button className="pvBtnGhost pvHoverGhost" onClick={() => navigate("/cursos")}>
              Ver todos
            </button>
          )}
        </div>

        {topCourses.length === 0 ? (
          <div className="pvEmpty">
            <div className="pvEmptyIcon" aria-hidden>
              📚
            </div>
            <div>
              <div className="pvEmptyTitle">Aún no estás inscrito en cursos</div>
              <div className="pvEmptySub">Explora cursos y empieza tu ruta de aprendizaje.</div>
            </div>
            <button className="pvBtnPrimary" onClick={() => navigate("/cursos")}>
              Explorar cursos
            </button>
          </div>
        ) : (
          <div className="pvCoursesGrid">
            {topCourses.map((c) => {
              const p = clamp(c?.progreso ?? 0);
              const last = c?.ultimo_acceso
                ? new Date(c.ultimo_acceso).toLocaleDateString("es-EC", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })
                : "—";

              return (
                <article key={c.curso_id} className="pvCourseCard pvHoverLift">
                  <div className="pvCourseTop">
                    <div className="pvCourseName" title={c.nombre}>
                      {c.nombre}
                    </div>
                    <span className="pvCoursePct">{Math.round(p)}%</span>
                  </div>

                  <div className="pvCourseMeta">
                    <span className="pvChip">Últ. acceso: {last}</span>
                    <span className="pvChip">
                      {p >= 100 ? "Completado" : p > 0 ? "En progreso" : "Sin iniciar"}
                    </span>
                  </div>

                  <div className="pvBarMini" aria-label={`Progreso ${c.nombre}`}>
                    <div className="pvBarMiniFill" style={{ width: `${p}%` }} />
                  </div>

                  <div className="pvCourseBottom">
                    <div className="pvCourseHint">
                      {p >= 80
                        ? "Ya casi terminas 🔥"
                        : p >= 50
                        ? "Buen ritmo 💪"
                        : p >= 20
                        ? "Vas arrancando 🚀"
                        : "Empieza con el primer módulo ✨"}
                    </div>

                    <button
                      className="pvBtnPrimary"
                      onClick={() => navigate(`/curso/${encodeURIComponent(c.codigo_curso)}`)}
                    >
                      Continuar
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function clamp(n) {
  const x = Number(n) || 0;
  return Math.max(0, Math.min(100, x));
}

const baseCss = `
  :root{
    --pv-ink:#0f172a;
    --pv-dim:#64748b;
    --pv-line:rgba(2,6,23,.08);
    --pv-card:rgba(255,255,255,.92);
    --pv-shadow:0 12px 36px rgba(2,6,23,.06);

    --pv-grad:linear-gradient(135deg, #3F51B5, #673AB7);
    --pv-grad-2:linear-gradient(90deg, #4f46e5, #7c3aed);

    --pv-soft:rgba(79,70,229,.10);
    --pv-soft-2:rgba(103,58,183,.10);
  }

  .pvPage{
    padding:26px 16px;
    max-width:1100px;
    margin:0 auto;
    min-height:100vh;
    background:
      radial-gradient(1200px 600px at 10% 0%, rgba(79,70,229,.10), transparent 60%),
      radial-gradient(900px 500px at 90% 10%, rgba(103,58,183,.10), transparent 60%),
      linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
  }

  .pvHoverLift{ transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease; }
  .pvHoverLift:hover{ transform: translateY(-2px); box-shadow: 0 16px 44px rgba(2,6,23,.10); border-color: rgba(79,70,229,.18); }
  .pvHoverGhost{ transition: background .16s ease, transform .16s ease; }
  .pvHoverGhost:hover{ background: rgba(79,70,229,.08); transform: translateY(-1px); }

  .pvMuted{ color:var(--pv-dim); }
  .pvMutedSmall{ color:var(--pv-dim); font-size:.86rem; }
  .pvMutedTiny{ color:var(--pv-dim); font-size:.75rem; }
  .pvDot{ color:#94a3b8; }
  .pvHideOnMobile{ display:inline; }
  .pvShowOnMobile{ display:none; }

  /* Header */
  .pvHeader{
    padding:18px;
    border-radius:18px;
    background:var(--pv-card);
    border:1px solid var(--pv-line);
    box-shadow:var(--pv-shadow);
    backdrop-filter: blur(8px);
    margin-bottom:14px;
  }
  .pvHeaderTop{ display:grid; grid-template-columns:72px 1fr; gap:14px; align-items:center; }
  .pvAvatarWrap{ display:flex; align-items:center; justify-content:center; }
  .pvAvatar{
    width:60px; height:60px; border-radius:18px;
    display:grid; place-items:center;
    font-size:1.7rem;
    background:linear-gradient(135deg, rgba(79,70,229,.14), rgba(103,58,183,.10));
    border:1px solid rgba(79,70,229,.20);
    box-shadow:0 10px 26px rgba(2,6,23,.06);
  }
  .pvHeaderInfo{ min-width:0; }
  .pvHeaderLine1{
    display:flex; align-items:flex-start; justify-content:space-between;
    gap:12px; flex-wrap:wrap;
  }
  .pvTitleWrap{ min-width:0; }
  .pvTitle{
    margin:0;
    font-size:1.55rem;
    font-weight:950;
    letter-spacing:-.02em;
    background:var(--pv-grad);
    -webkit-background-clip:text;
    -webkit-text-fill-color:transparent;
  }
  .pvSub{
    margin-top:6px;
    color:#475569;
    font-size:.95rem;
    display:flex; gap:8px; align-items:center; flex-wrap:wrap;
  }
  .pvHeaderActions{ display:flex; gap:10px; }

  .pvRoles{ display:flex; gap:8px; flex-wrap:wrap; margin-top:10px; }
  .pvRolePill{
    padding:6px 10px; border-radius:999px;
    border:1px solid rgba(2,6,23,.10);
    background:rgba(255,255,255,.85);
    font-size:.84rem; color:var(--pv-ink);
  }

  /* Stats */
  .pvStats{ display:grid; gap:12px; margin-bottom:14px; }
  .pvStatsRow{ display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:12px; }
  .pvStatCard{
    background:var(--pv-card);
    border-radius:16px;
    padding:14px;
    border:1px solid var(--pv-line);
    box-shadow:var(--pv-shadow);
    display:flex; gap:12px; align-items:center;
  }
  .pvStatIcon{
    width:46px; height:46px; border-radius:14px;
    display:grid; place-items:center;
    border:1px solid rgba(2,6,23,.08);
    font-size:1.25rem;
  }
  .pvCoinIcon{ background:rgba(15, 95, 184, .10); }
  .pvStreakIcon{ background:rgba(255, 87, 34, .12); }
  .pvStatBody{ flex:1; min-width:0; }
  .pvStatValue{ font-size:1.30rem; font-weight:950; color:var(--pv-ink); line-height:1.1; }
  .pvStatLabel{ font-size:.92rem; font-weight:800; color:#334155; margin-top:2px; }
  .pvStatHint{ font-size:.82rem; color:var(--pv-dim); margin-top:2px; }

  /* Progress */
  .pvProgressCard{
    background:var(--pv-card);
    border-radius:16px;
    padding:14px;
    border:1px solid var(--pv-line);
    box-shadow:var(--pv-shadow);
  }
  .pvProgressHead{
    display:flex; justify-content:space-between; align-items:flex-end;
    gap:12px; margin-bottom:10px;
  }
  .pvProgressTitle{ font-weight:950; color:var(--pv-ink); font-size:1.02rem; }
  .pvProgressSub{ color:var(--pv-dim); font-size:.86rem; margin-top:2px; }
  .pvProgressPctWrap{ text-align:right; }
  .pvProgressPct{ font-weight:950; color:#4f46e5; font-size:1.18rem; line-height:1; }

  /* Barra PRO (más pro que la anterior) */
  .pvBarPro{
    position:relative;
    height:12px;
    border-radius:999px;
    background:rgba(15,23,42,.08);
    overflow:hidden;
    border:1px solid rgba(2,6,23,.06);
  }
  .pvBarPro::after{
    content:"";
    position:absolute; inset:0;
    background:linear-gradient(90deg, transparent, rgba(255,255,255,.35), transparent);
    transform:translateX(-60%);
    animation: pvShine 2.6s ease-in-out infinite;
    pointer-events:none;
    opacity:.55;
  }
  .pvBarProFill{
    height:100%;
    width:0%;
    border-radius:999px;
    background:var(--pv-grad-2);
    transition:width .45s cubic-bezier(.2,.8,.2,1);
    box-shadow:0 10px 26px rgba(79,70,229,.18);
  }
  @keyframes pvShine{
    0%{ transform:translateX(-70%); opacity:.0; }
    15%{ opacity:.55; }
    55%{ opacity:.35; }
    100%{ transform:translateX(120%); opacity:0; }
  }

  .pvProgressMeta2{
    display:flex;
    gap:10px;
    flex-wrap:wrap;
    margin-top:12px;
  }
  .pvPill{
    display:flex;
    gap:8px;
    align-items:center;
    padding:7px 10px;
    border-radius:999px;
    border:1px solid rgba(2,6,23,.10);
    background:rgba(255,255,255,.72);
  }
  .pvPillK{ color:var(--pv-dim); font-size:.82rem; font-weight:700; }
  .pvPillV{ color:var(--pv-ink); font-size:.88rem; font-weight:950; }

  .pvProgressFoot{ margin-top:10px; }

  /* Card + section */
  .pvCard{
    background:var(--pv-card);
    padding:16px;
    border-radius:18px;
    box-shadow:var(--pv-shadow);
    border:1px solid var(--pv-line);
    backdrop-filter: blur(8px);
  }
  .pvSectionHead{
    display:flex; justify-content:space-between; align-items:flex-start;
    gap:12px; flex-wrap:wrap; margin-bottom:12px;
  }
  .pvSectionTitle{ margin:0; font-size:1.10rem; font-weight:950; color:var(--pv-ink); }
  .pvSectionSub{ margin:6px 0 0; color:var(--pv-dim); font-size:.9rem; }

  /* Courses */
  .pvCoursesGrid{ display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:12px; }
  .pvCourseCard{
    background:rgba(255,255,255,.95);
    border:1px solid rgba(2,6,23,.08);
    border-radius:16px;
    padding:14px;
    box-shadow:0 10px 26px rgba(2,6,23,.05);
    display:grid; gap:10px;
  }
  .pvCourseTop{ display:flex; justify-content:space-between; align-items:flex-start; gap:10px; }
  .pvCourseName{
    font-weight:950; color:var(--pv-ink); font-size:1.02rem;
    white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
  }
  .pvCoursePct{
    font-weight:950;
    color:#4f46e5;
    background:rgba(79,70,229,.10);
    border:1px solid rgba(79,70,229,.20);
    padding:4px 8px;
    border-radius:999px;
    font-size:.82rem;
    white-space:nowrap;
  }
  .pvCourseMeta{ display:flex; gap:8px; flex-wrap:wrap; }
  .pvChip{
    font-size:.78rem;
    color:#334155;
    background:rgba(15,23,42,.06);
    border:1px solid rgba(2,6,23,.06);
    border-radius:999px;
    padding:5px 10px;
  }

  .pvBarMini{
    height:10px;
    border-radius:999px;
    background:rgba(15,23,42,.08);
    overflow:hidden;
    border:1px solid rgba(2,6,23,.06);
  }
  .pvBarMiniFill{
    height:100%;
    width:0%;
    border-radius:999px;
    background:var(--pv-grad);
    transition:width .35s ease;
  }

  .pvCourseBottom{ display:flex; justify-content:space-between; align-items:center; gap:10px; flex-wrap:wrap; }
  .pvCourseHint{ color:var(--pv-dim); font-size:.88rem; }

  /* Buttons */
  .pvBtnPrimary{
    background:var(--pv-grad);
    color:#fff;
    border:none;
    padding:10px 14px;
    border-radius:12px;
    cursor:pointer;
    font-size:.92rem;
    font-weight:950;
    box-shadow:0 12px 26px rgba(79,70,229,.16);
    white-space:nowrap;
  }
  .pvBtnGhost{
    background:transparent;
    color:#4f46e5;
    border:1px solid rgba(79,70,229,.22);
    padding:9px 12px;
    border-radius:12px;
    cursor:pointer;
    font-weight:950;
    font-size:.9rem;
    white-space:nowrap;
  }
  .pvBtnDanger{
    background:#e11d48;
    color:#fff;
    border:none;
    padding:12px 20px;
    border-radius:10px;
    cursor:pointer;
    font-weight:900;
  }

  /* Empty */
  .pvEmpty{
    display:grid;
    grid-template-columns:56px 1fr auto;
    gap:14px;
    align-items:center;
    padding:14px;
    border-radius:16px;
    border:1px dashed rgba(2,6,23,.18);
    background:rgba(255,255,255,.72);
  }
  .pvEmptyIcon{
    width:56px; height:56px; border-radius:16px;
    display:grid; place-items:center;
    font-size:1.6rem;
    background:rgba(79,70,229,.10);
    border:1px solid rgba(79,70,229,.18);
  }
  .pvEmptyTitle{ font-weight:950; color:var(--pv-ink); }
  .pvEmptySub{ color:var(--pv-dim); font-size:.9rem; margin-top:3px; }

  /* Error */
  .pvCardCenter{
    background:#fff;
    padding:32px;
    border-radius:20px;
    box-shadow:0 8px 32px rgba(0,0,0,.1);
    border:1px solid rgba(0,0,0,.06);
    max-width:520px;
    margin:0 auto;
    text-align:center;
  }
  .pvErrorBorder{ border:2px solid #e11d48 !important; }

  @media (max-width: 900px){
    .pvHideOnMobile{ display:none; }
    .pvShowOnMobile{ display:inline; }
    .pvHeaderTop{ grid-template-columns:64px 1fr; }
    .pvStatsRow{ grid-template-columns:1fr; }
    .pvCoursesGrid{ grid-template-columns:1fr; }
  }

  @media (max-width: 520px){
    .pvEmpty{ grid-template-columns:1fr; }
    .pvHeaderLine1{ align-items:flex-start; }
  }
`;
